import type { VNode } from 'vue';
import PtMessageBox from '@ui/message-box';
import PtButton from '@uiv1/components/button';
import PtIcon from '@uiv1/components/icon';
import styles from './index.module.scss';
import i18n from '@assets/i18n';
import planLimitConfig, { Limit } from './planLimitConfig';
import intercomService from '@assets/services/intercom.service';
import { useNPPackageInfo } from '@models/package/package';
import useEventBus from '@assets/plugins/eventBus';
import analyticsService from '@assets/services/analytics.service';
import { useAccountInfo } from '@models/account/useAccountInfo';
import store from '@/store';
import { usePackageInfo } from '@models/package/usePackageInfo';

const { packageInfo, currentOriginProfile } = usePackageInfo();

let inActive: boolean = false;
export default function useActiveProduct() {
    const { eventBus } = useEventBus();
    const { currentPackageInfo, profileNpPackageInfo } = useNPPackageInfo();
    const { account } = useAccountInfo();
    const showUpGradeDialog = ({
        limitType,
        privilege,
        product
    }: {
        limitType: keyof typeof planLimitConfig.ptxLimits | keyof typeof planLimitConfig.ptiLimits;
        privilege: number | boolean;
        product: 'pti' | 'ptx';
    }) => {
        if (inActive) return;
        inActive = true;
        const { ptiLimits, ptxLimits, commonLimits } = planLimitConfig;
        const currentLimit: Limit =
            commonLimits[limitType] ?? ptxLimits[limitType] ?? ptiLimits[limitType];
        if (!currentLimit) return;

        const title = currentLimit.title
            ? i18n.global.t(currentLimit.title)
            : i18n.global.t('common.no_auth_tip_title1');
        const desc = currentLimit.desc
            ? i18n.global.t(currentLimit.desc, {
                  num: privilege,
                  price: account.value?.area?.toUpperCase() === 'EN' ? '$49' : '¥4980円'
              })
            : i18n.global.t('common.general_upgrade_con');

        const cancel = async () => {
            const overlay = document.querySelector(`.${styles.messageBoxBody}`);
            const closeBtn = overlay?.querySelector(
                '.pt-message-box__headerbtn'
            ) as HTMLButtonElement;
            closeBtn?.click?.();
        };

        const onHandleContactUs = () => {
            cancel();
            intercomService?.contact();
        };

        const onHandleUpGrade = () => {
            eventBus.$emit('upgrade', { type: limitType });
            cancel();
            // sendEvent();
        };

        const sendEvent = () => {
            const {
                insight,
                insightIsGrowth,
                insightIsTrial,
                experience,
                experienceIsGrowth,
                experienceIsTrial
            } = currentPackageInfo?.value || {};

            let current_plan = 'Free';
            if (insightIsGrowth && experienceIsGrowth) current_plan = 'Bundle Growth';
            else if (insightIsGrowth) current_plan = 'Insight Growth';
            else if (experienceIsGrowth) current_plan = 'Experience Growth';
            else if (insightIsTrial && experienceIsTrial) current_plan = 'Bundle Trial';
            else if (insightIsTrial) current_plan = 'Insight Trial';
            else if (experienceIsTrial) current_plan = 'Experience Trial';
            const current_plan_pv = [
                'Bundle Growth',
                'Insight Growth',
                'Insight Trial',
                'Free'
            ].includes(current_plan)
                ? insight?.privilege.monthlyPV
                : experience?.privilege.monthlyPV;

            analyticsService
                .usePTX('upgrade_click', {
                    product,
                    position: limitType,
                    current_plan,
                    current_plan_pv
                })
                .track();
        };

        PtMessageBox({
            showClose: true,
            showCancelButton: false,
            showConfirmButton: false,
            title: 'Confirmation',
            customClass: styles.messageBoxBody,
            callback: () => (inActive = false),
            message: (
                <>
                    <div class={styles.container}>
                        <p class={styles.title}>
                            <PtIcon icon="pt-icon--warn" />
                            {title}
                        </p>
                        <p class={styles.desc}>{desc}</p>
                        <div class={styles.btnArea}>
                            <PtButton
                                size="medium"
                                type="outline"
                                color="darkblue"
                                onClick={onHandleContactUs}
                            >
                                {i18n.global.t('form.link.contact_us')}
                            </PtButton>
                            <PtButton size="medium" color="orange" onClick={onHandleUpGrade}>
                                {i18n.global.t('common.button_upgrade')}
                            </PtButton>
                        </div>
                    </div>
                </>
            ) as VNode
        })
            .then(e => {})
            .catch(e => {});
    };

    const checkAuth = ({
        limitType,
        currentUsage,
        productType,
        editType,
        isNpPackage = true
    }: {
        limitType: keyof typeof planLimitConfig.ptxLimits | keyof typeof planLimitConfig.ptiLimits;
        currentUsage?: number | null;
        productType: 'pti' | 'ptx';
        editType?: 'EDIT' | 'CREATE';
        isNpPackage: boolean;
    }) => {
        if (!limitType) return null;
        const { ptiLimits, ptxLimits, commonLimits, oldKeyMapping } = planLimitConfig;
        const authKey = oldKeyMapping[limitType] || limitType; //兼容老版本权限项
        const currentLimit: Limit =
            commonLimits[authKey] ?? ptxLimits[authKey] ?? ptiLimits[authKey];
        const usageKey = currentLimit?.usageKey?.[productType] ?? authKey;

        // 兼容旧套餐
        if (!isNpPackage) {
            const _res = checkOldAuth(
                currentLimit?.oldAuthKey ?? currentLimit?.usageKey?.[productType] ?? authKey,
                editType || 'CREATE'
            );
            return {
                ..._res,
                authKey
            };
        }

        const {
            commonPrivilege,
            commonPrivilegeUsage,
            ptxPrivilege,
            ptxPrivilegeUsage,
            ptiPrivilege,
            ptiPrivilegeUsage
        }: any = profileNpPackageInfo.value || {};

        const privilege: number | boolean =
            commonPrivilege?.[authKey] ?? ptxPrivilege?.[authKey] ?? ptiPrivilege?.[authKey];
        const privilegeUsage: number | boolean =
            currentUsage !== null && currentUsage !== undefined
                ? currentUsage
                : commonPrivilegeUsage?.[usageKey] ??
                  ptxPrivilegeUsage?.[usageKey] ??
                  ptiPrivilegeUsage?.[usageKey];
        const effective =
            typeof privilege === 'number'
                ? privilege === -1 ||
                  (editType === 'EDIT' ? privilegeUsage <= privilege : privilegeUsage < privilege)
                : privilege;
        return {
            limitType,
            authKey,
            privilege,
            privilegeUsage,
            effective
        };
    };

    const getPtxOldPackageInfo = () => {
        const {
            sid,
            ptxPackageId,
            startTime,
            endTime,
            ptxPrivilege,
            ptxPrivilegeUsage,
            commonPrivilege,
            commonPrivilegeUsage
        } = profileNpPackageInfo.value || {};
        return {
            sid,
            packageId: ptxPackageId,
            startTime,
            endTime,
            status: 1,
            privilege: {
                monthlyActiveUsers: -1,
                userGroups: ptxPrivilege?.userGroups,
                goals: commonPrivilege?.conversions,
                campaigns: ptxPrivilege?.campaigns,
                useABTest: ptxPrivilege?.useCampaignAbTest,
                useCodeMode: ptxPrivilege?.useCampaignCodeMode,
                useCSVDownload: true,
                useAI: ptxPrivilege?.useCampaignAI,
                hidePopupPtxLogo: null
            },
            packageBaseConf: {
                packageName: ptxPackageId,
                packageType: 1,
                area: 0,
                level: 0
            },
            usage: {
                monthlyActiveUsers: 1,
                userGroups: ptxPrivilegeUsage?.userGroups,
                goals: commonPrivilegeUsage?.ptxConversions,
                campaigns: ptxPrivilegeUsage?.campaigns
            }
        };
    };

    return { showUpGradeDialog, checkAuth, getPtxOldPackageInfo };
}

//  旧套餐校验
const checkOldAuth = (
    privilegeKey:
        | keyof typeof planLimitConfig.ptxLimits
        | keyof typeof planLimitConfig.commonLimits,
    editType: 'EDIT' | 'CREATE'
) => {
    let result = {
        effective: true
    };

    if (!packageInfo.value) {
        return {
            effective: false
        };
    }

    // 老套餐查看热图权限放开
    if (privilegeKey === 'useCampaignHeatmap') {
        return result;
    }

    const privilege: any = packageInfo.value.privilege;
    const usage: any = packageInfo.value.usage;

    const fieldType = privilege[privilegeKey] ? typeof privilege[privilegeKey] : null;
    const editTypeList = ['CREATE', 'EDIT']; //到量的时候可以编辑但是不能创建，所以edit的时候判断小于privilege + 1
    let _effective = false;
    if (fieldType === 'number') {
        _effective =
            Number(privilege[privilegeKey]) === -1 ||
            Number(usage[privilegeKey]) <
                Number(privilege[privilegeKey] + editTypeList.findIndex(item => item === editType));
    } else if (fieldType === 'boolean') {
        _effective = privilege[privilegeKey];
    }
    return {
        privilege: privilege[privilegeKey],
        effective: _effective
    };
};
